import { useCallback, useState } from 'react';

import { ORDERS_BLOTTER_PREFIX } from '@src/containers/Blotters/Orders/tokens';
import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
} from '@talos/kyoko';
import { selectCurrentLayout } from '../../../components/AppLayout/AppLayoutSlice';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { useAppStateSelector } from '../../../providers/AppStateProvider';
import type { OrderTabProps } from '../../../types/LayoutConfig';
import { OPEN_ORDERS_TAB_ID, ORDER_TABS_EXCLUDED_IDS, useDefaultOrderTabs } from './defaults';
import { FilteredOrders } from './FilteredOrders';
import { OpenOrders } from './OpenOrders';

export type { OrderTabProps };

const tabLabeler = tabLabelerEnumerated('Orders');
function getBlotterID(tabID: string | undefined) {
  return `${ORDERS_BLOTTER_PREFIX}/${tabID}`;
}

export function Orders() {
  const mixpanel = useMixpanel();
  const layoutId = useAppStateSelector(selectCurrentLayout)?.id;
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  }, [mixpanel]);

  const { tabs: defaultPrincipalOrderTabs, newTabDefaults, openOrdersDefaultFilter } = useDefaultOrderTabs();

  const persistedTabs = usePersistedTabs<OrderTabProps>(ORDERS_BLOTTER_PREFIX, {
    defaultInitialItems: defaultPrincipalOrderTabs,
    defaultInitialSelectedIndex: 0,
    excludedIDs: ORDER_TABS_EXCLUDED_IDS,
    onSelect: handleSelect,
  });

  const tabs = useTabs<OrderTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map(tab => (
          <AccordionGroup key={tab.id}>
            {tab.id === OPEN_ORDERS_TAB_ID ? (
              <OpenOrders key={tab.id} defaultFilter={tab.defaultFilter ?? openOrdersDefaultFilter} />
            ) : (
              <FilteredOrders
                key={tab.id}
                blotterID={`${ORDERS_BLOTTER_PREFIX}/${tab.id}`}
                tabLabel={tab.label}
                defaultColumns={tab.defaultColumns ?? newTabDefaults.defaultColumns}
                defaultFilter={tab.defaultFilter ?? newTabDefaults.defaultFilter}
                onCloneTab={handleCloneTab}
                initialIsOpen={initialFiltersOpen}
                layoutId={layoutId}
              />
            )}
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}

export default Orders;
